import { Injectable, inject } from '@angular/core';
import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import { Subject } from 'rxjs';

import { HubConnectionFactory } from './hub-connection.factory';
import { SignalRHubNames } from './signalr-hub-names';

export interface TicketCreatedEvent {
  ticketId: string;
  ticketNumber: number;
  conversationId: string;
  subject: string;
}

export interface TicketStatusChangedEvent {
  ticketId: string;
  previousStatus: string;
  newStatus: string;
}

export interface TicketAssignedEvent {
  ticketId: string;
  previousAssigneeUserId: string | null;
  newAssigneeUserId: string | null;
}

export interface TicketNoteAddedEvent {
  ticketId: string;
  noteId: string;
  authorUserId: string;
}

export interface TicketAttachmentAddedEvent {
  ticketId: string;
  attachmentId: string;
  fileName: string;
}

/**
 * Connection lifecycle plus the five server-to-client events (Phase 3B) for TicketingHub:
 * TicketCreated, TicketStatusChanged, TicketAssigned, TicketNoteAdded,
 * TicketAttachmentAdded -- mirrors ConversationsHubService.
 */
@Injectable({ providedIn: 'root' })
export class TicketingHubService {
  private readonly connection: HubConnection = inject(HubConnectionFactory).create(SignalRHubNames.ticketing);

  private readonly ticketCreatedSubject = new Subject<TicketCreatedEvent>();
  private readonly ticketStatusChangedSubject = new Subject<TicketStatusChangedEvent>();
  private readonly ticketAssignedSubject = new Subject<TicketAssignedEvent>();
  private readonly ticketNoteAddedSubject = new Subject<TicketNoteAddedEvent>();
  private readonly ticketAttachmentAddedSubject = new Subject<TicketAttachmentAddedEvent>();

  readonly ticketCreated$ = this.ticketCreatedSubject.asObservable();
  readonly ticketStatusChanged$ = this.ticketStatusChangedSubject.asObservable();
  readonly ticketAssigned$ = this.ticketAssignedSubject.asObservable();
  readonly ticketNoteAdded$ = this.ticketNoteAddedSubject.asObservable();
  readonly ticketAttachmentAdded$ = this.ticketAttachmentAddedSubject.asObservable();

  constructor() {
    this.connection.on('TicketCreated', (payload: TicketCreatedEvent) => this.ticketCreatedSubject.next(payload));
    this.connection.on('TicketStatusChanged', (payload: TicketStatusChangedEvent) => this.ticketStatusChangedSubject.next(payload));
    this.connection.on('TicketAssigned', (payload: TicketAssignedEvent) => this.ticketAssignedSubject.next(payload));
    this.connection.on('TicketNoteAdded', (payload: TicketNoteAddedEvent) => this.ticketNoteAddedSubject.next(payload));
    this.connection.on('TicketAttachmentAdded', (payload: TicketAttachmentAddedEvent) => this.ticketAttachmentAddedSubject.next(payload));
  }

  async start(): Promise<void> {
    if (this.connection.state === HubConnectionState.Disconnected) {
      await this.connection.start();
    }
  }

  async stop(): Promise<void> {
    await this.connection.stop();
  }
}
